export default class ArticleSummary extends HTMLElement {
  private visible = new Set();
  private ticking = false;

  constructor() {
    super();
  }

  getLinks() {
    return this.querySelectorAll<HTMLLIElement>('li[data-target-section-id]');
  }

  getSections() {
    return document.querySelectorAll<HTMLDivElement>('.anchor-extend-container .anchor-extend[data-anchor-name]');
  }

  updateLinks() {
    const links = this.getLinks();

    links.forEach(link => {
      const { targetSectionId } = link.dataset;
      link.classList.remove('active');

      // supprimer l'attribut aria-current pour tous les liens
      link.removeAttribute('aria-current');

      // if (this.visible.has(targetSectionId)) link.classList.add('active');

      // cacher les liens vers les sections qui ne sont pas visibles
      const section = document.querySelector<HTMLElement>(`.anchor-extend[data-anchor-slug="${targetSectionId}"]`);
      const sectionVisible = section ? section.offsetParent !== null : false;
      link.hidden = !sectionVisible;

      if (this.visible.has(targetSectionId)) {
        // Ne pas activer le lien si le chapitre suivant (.js-section-page) est caché
        const container = section?.closest<HTMLElement>('.anchor-extend-container');
        const chapter = container?.nextElementSibling as HTMLElement | null;
        const chapterHidden = chapter?.classList.contains('js-section-page') && !chapter.classList.contains('section-page--active');

        if (!chapterHidden) link.classList.add('active');

        // Ajouter aria-current="true" pour le lien actif
        if (!chapterHidden) link.setAttribute('aria-current', 'true');
      }      
    });

    this.updateDecorationState(links);
  }

  private getLevel2ItemsFor(level1Item: HTMLLIElement): HTMLLIElement[] {
    const list = level1Item.parentElement;
    if (!list) return [];

    const items: HTMLLIElement[] = [];
    let nextSibling = level1Item.nextElementSibling as HTMLLIElement | null;

    while (nextSibling) {
      if (nextSibling.classList.contains('level1')) break;
      if (nextSibling.classList.contains('level2')) items.push(nextSibling);
      nextSibling = nextSibling.nextElementSibling as HTMLLIElement | null;
    }

    return items;
  }

  private toggleLevel2Visibility(level1Item: HTMLLIElement) {
    const children = this.getLevel2ItemsFor(level1Item);
    if (!children.length) return;

    const isCurrentlyVisible = children.some((item) => !item.hidden);

    children.forEach((item) => {
      item.hidden = true;
    });

    if (level1Item.classList.contains('active') && isCurrentlyVisible) {
      return;
    }

    children.forEach((item) => {
      item.hidden = false;
    });
  }

  showHideSections() {
    const links = this.getLinks();

    links.forEach(link => {
      const { targetSectionId } = link.dataset;
      const anchor = document.querySelector<HTMLElement>(`.anchor-extend[data-anchor-slug="${targetSectionId}"]`);
      const section = anchor?.closest<HTMLElement>('.anchor-extend-container');

      if (!section) return;

      link.addEventListener('click', () => {
        this.revealSection(section);

        if (link.classList.contains('level1')) {
          this.toggleLevel2Visibility(link);
        }
      });
    });
  }

  chapterNavigation() {
    const chapters = Array.from(document.querySelectorAll<HTMLElement>('.js-section-page'));

    chapters.forEach((chapter, index) => {
      // Lien "suivant" : texte = data-anchor-name de la 1ère ancre du chapitre suivant
      const nextLink = chapter.querySelector<HTMLAnchorElement>('.chapitre-nav__next a');
      const nextChapter = chapters[index + 1];
      if (nextLink && nextChapter) {
        const anchorName = nextChapter.previousElementSibling
          ?.querySelector<HTMLElement>('.anchor-extend[data-anchor-name]')
          ?.dataset.anchorName;

        const anchorLink = nextChapter.previousElementSibling
          ?.querySelector<HTMLElement>('.anchor-extend[data-anchor-name]')
          ?.id;
        
          if (anchorName) nextLink.querySelector('span')!.textContent = anchorName;
          if (anchorLink) nextLink.href = `#${anchorLink}`;

        nextLink.addEventListener('click', (e) => {
          // e.preventDefault();
          this.activateChapter(nextChapter);
        });
      }

      // Lien "précédent" : activer le chapitre précédent au clic
      const prevLink = chapter.querySelector<HTMLAnchorElement>('.chapitre-nav__prev a');
      const prevChapter = chapters[index - 1];
      if (prevLink && prevChapter) {
        const anchorLink = prevChapter.previousElementSibling
          ?.querySelector<HTMLElement>('.anchor-extend[data-anchor-name]')
          ?.id;
        
          if (anchorLink) prevLink.href = `#${anchorLink}`;
        
          prevLink.addEventListener('click', (e) => {
          // e.preventDefault();
          this.activateChapter(prevChapter);
        });
      }
    });
  }

  private activateChapter(chapter: HTMLElement): void {
    if (chapter.classList.contains('section-page--active')) return;

    document.querySelectorAll<HTMLElement>('.js-section-page.section-page--active')
      .forEach(el => el.classList.remove('section-page--active'));

    chapter.classList.add('section-page--active');
    // chapter.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.onWindowScroll();
  }

  private revealSection(section: HTMLElement): void {
    // Le chapitre (.js-section-page) est le sibling suivant l'élément anchor-extend-container
    const chapter = section.nextElementSibling as HTMLElement;
    if (!chapter?.classList.contains('js-section-page')) return;
    this.activateChapter(chapter);
  }

  private updateDecorationState(links: NodeListOf<HTMLLIElement>) {
    const decoration = this.parentElement?.querySelector('.article-summary-decoration');
    if (!decoration) {
      return;
    }

    const lastLink = links[links.length - 1];
    if (lastLink?.classList.contains('active')) {
      decoration.classList.add('active');
    } else {
      decoration.classList.remove('active');
    }
  }

  getOffsets() {
    const headerHeightVars = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const offset = parseInt(headerHeightVars, 10);

    return {
      viewportTop: offset,
      viewportBottom: window.innerHeight - offset,
    };
  }

  setVisibleElement() {
    const offsets = this.getOffsets();
    // On ne considère que les éléments visibles (offsetParent !== null) pour éviter les sections cachées par des conditions d'affichage
    // On exclut aussi les sections dont le .anchor-extend-container est suivi d'un .js-section-page inactif
    const elements = Array.from(this.getSections()).filter(el => {
      if (el.offsetParent === null) return false;
      const container = el.closest<HTMLElement>('.anchor-extend-container');
      const chapter = container?.nextElementSibling as HTMLElement | null;
      if (chapter?.classList.contains('js-section-page') && !chapter.classList.contains('section-page--active')) return false;
      return true;
    });

    const passed = elements.filter(el => {
        const rect = el.getBoundingClientRect();
        return rect.top <= offsets.viewportTop;
    });

    this.visible.clear();

    if (passed.length) {
      const current = passed[passed.length - 1];
      this.visible.add(current.dataset.anchorSlug);
    } else {
      const first = elements[0];
      if (first) this.visible.add(first.dataset.anchorSlug);
    }
  }

  onWindowScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.setVisibleElement();
        this.updateLinks();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  init() {
    window.addEventListener('scroll', this.onWindowScroll.bind(this));
    this.showHideSections();
    this.chapterNavigation();
    this.onWindowScroll();
  }

  connectedCallback() {
    this.init();
    window.addEventListener('load', () => {
      this.onWindowScroll();
    });
  }
}